import DrawerContent from 'layout/drawer/content/DrawerContent.tsx';
import { Drawer } from 'layout/drawer/Drawer.styles.tsx';
import { ReactNode, useState } from 'react';
import { useStore } from '../../store/store.ts';
import { collapsedDrawerWidth, drawerWidth, toolbarSpacing } from '../../consts/appConsts.ts';
import DrawerToggleButton from '../../components/button/DrawerToggleButton.tsx';
import DrawerResizeHandle from './DrawerResizeHandle.tsx';
import { PaperProps as MuiPaperProps } from '@mui/material/Paper';

type Props = {
  open: boolean;
  drawerContent?: ReactNode;
  anchor: 'left' | 'right';
  toggleDrawer: () => void;
  variant?: 'permanent' | 'temporary' | 'persistent';
  collapsedWidth?: number;
  expandedWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  paperProps?: Partial<MuiPaperProps>;
  resizable?: boolean;
};

const MainDrawer = ({
  anchor = 'left',
  open,
  drawerContent = <DrawerContent />,
  toggleDrawer,
  variant = 'permanent',
  collapsedWidth = collapsedDrawerWidth,
  expandedWidth = drawerWidth,
  minWidth,
  maxWidth,
  paperProps,
  resizable = true,
}: Props) => {
  const { setRightDrawerWidth, setLeftDrawerWidth } = useStore();
  const setWidth = anchor === 'left' ? setLeftDrawerWidth : setRightDrawerWidth;
  const toolbarHeight = useStore().toolbarHeight;
  const [drawerWidth, setDrawerWidth] = useState(expandedWidth);

  const updateDrawerWidth = (width: number) => {
    setDrawerWidth(Math.max(Math.min(width, maxWidth ?? Infinity), minWidth ?? 0));
  };

  return (
    <Drawer
      variant={variant}
      sx={{
        [`& .MuiDrawer-paper`]: { paddingTop: `${toolbarHeight + toolbarSpacing}px`, overflow: 'visible' },
      }}
      open={open}
      className="overflow-visible relative"
      anchor={anchor}
      collapsedWidth={collapsedWidth}
      expandedWidth={drawerWidth}
      PaperProps={paperProps}
    >
      <div className={`flex h-full flex-1 ${anchor === 'left' ? 'flex-row-reverse' : 'flex-row'}`}>
        {open && resizable && (
          <DrawerResizeHandle anchor={anchor} onResize={updateDrawerWidth} onDragEnd={() => setWidth(drawerWidth)} />
        )}
        {drawerContent}
      </div>
      <DrawerToggleButton
        open={open}
        toggleDrawer={toggleDrawer}
        anchor={anchor}
        collapsedWidth={collapsedWidth}
        expandedWidth={expandedWidth}
      />
    </Drawer>
  );
};

export default MainDrawer;
